import React, { useEffect, useMemo } from 'react';

import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';

import { dashboardIcon } from 'c-main/icons';

import { PermissionName } from 'c-sdk';
import { Box, Tab } from '@mui/material';
import { Tabs } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import FirstTab from 'c-dashboard-module/Tabs/FirstTab.main';
import SecondTab from 'c-dashboard-module/Tabs/SecondTab.main';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';

const MainDashboardPage = () => {
    const [value, setValue] = React.useState('1');
    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };
    const {
        start: pieAllImpactsStart,
        data: pieAllImpactsData,
        isLoading,
    } = useAPIClientRequest(ApiClient.Reports.dashboard);
    const {
        start: campaignImpactsStart,
        data: campaignImpactsData,
        isLoading: campLoading,
    } = useAPIClientRequest(ApiClient.Reports.dashboard);
    const {
        start: impactsByEnvironmentStart,
        data: impactsByEnvironmentData,
        isLoading: envLoading,
    } = useAPIClientRequest(ApiClient.Reports.dashboard);
    const {
        start: secondTabStart,
        data: secondTabData,
        isLoading: secondTabLoading,
    } = useAPIClientRequest(ApiClient.Reports.dashboardLive);
    const loading = useMemo(
        () => isLoading || campLoading || envLoading,
        [isLoading, campLoading, envLoading],
    );
    useEffect(() => {
        const oneYearAgo = new Date();
        oneYearAgo.setMonth(0);
        oneYearAgo.setDate(1);
        oneYearAgo.setHours(0, 0, 0, 0);
        pieAllImpactsStart({
            graphType: 'pie',
            metrics: [
                { field: 'expected_impacts_in_schedule', aggregation: 'sum' },
                { field: 'impacts_received_in_schedule', aggregation: 'sum' },
                { field: 'impacts_received_out_of_schedule', aggregation: 'sum' },
                { field: 'impacts_received_unbooked', aggregation: 'sum' },
            ],
            dimensions: [{ field: 'display_owner' }],
            filters: [
                {
                    field: 'playout_start_date',
                    operator: 'gte',
                    value: oneYearAgo.toISOString(),
                },
                { field: 'inside_campaign_settings_date', operator: 'eq', value: 1 },
                { field: 'reference', operator: 'not_like', value: '%Programmatic%' },
            ],
            limit: 99999999999999,
        });
        impactsByEnvironmentStart({
            graphType: 'bar',
            metrics: [
                { field: 'expected_impacts_in_schedule', aggregation: 'sum' },
                { field: 'impacts_received_in_schedule', aggregation: 'sum' },
                { field: 'impacts_received_out_of_schedule', aggregation: 'sum' },
                { field: 'impacts_received_unbooked', aggregation: 'sum' },
            ],
            dimensions: [{ field: 'display_env' }],
            filters: [
                {
                    field: 'playout_start_date',
                    operator: 'gte',
                    value: oneYearAgo.toISOString(),
                },
                { field: 'inside_campaign_settings_date', operator: 'eq', value: 1 },
                { field: 'reference', operator: 'not_like', value: '%Programmatic%' },
            ],
            limit: 99999999999999,
        });
        campaignImpactsStart({
            graphType: 'bar',
            metrics: [
                { field: 'expected_impacts_in_schedule', aggregation: 'sum' },
                { field: 'impacts_received_in_schedule', aggregation: 'sum' },
                { field: 'impacts_received_out_of_schedule', aggregation: 'sum' },
                { field: 'impacts_received_unbooked', aggregation: 'sum' },
            ],
            dimensions: [{ field: 'campaign_id' }],
            filters: [
                {
                    field: 'playout_start_date',
                    operator: 'gte',
                    value: oneYearAgo.toISOString(),
                },
                { field: 'inside_campaign_settings_date', operator: 'eq', value: 1 },
                { field: 'reference', operator: 'not_like', value: '%Programmatic%' },
            ],
            limit: 99999999999999,
        });
        secondTabStart();
    }, [campaignImpactsStart, impactsByEnvironmentStart, pieAllImpactsStart, secondTabStart]);

    return (
        <TabContext value={value}>
            <Box
                sx={{ borderBottom: 1, borderColor: 'divider' }}
                display="flex"
                justifyContent="center"
            >
                <Tabs value={value} onChange={handleChange}>
                    <Tab
                        label="YTD Aggregate"
                        value="1"
                        sx={{ fontSize: '1.2rem', py: 2, px: 3 }}
                    />
                    <Tab
                        label="Live Campaigns"
                        value="2"
                        sx={{ fontSize: '1.2rem', py: 2, px: 3 }}
                    />
                </Tabs>
            </Box>
            <TabPanel value="1">
                <FirstTab
                    loading={loading}
                    campaignImpactsData={campaignImpactsData}
                    impactsByEnvironmentData={impactsByEnvironmentData}
                    pieAllImpactsData={pieAllImpactsData}
                />
            </TabPanel>
            <TabPanel value="2">
                <SecondTab data={secondTabData?.data ?? {}} isLoading={secondTabLoading} />
            </TabPanel>
        </TabContext>
    );
};
const setup: PageConfig = {
    id: 'DashboardHomePage',
    component: MainDashboardPage,
    systemSearch: {
        icon: dashboardIcon,
        saveAsRecentPage: true,
        title: 'Pages.DashboardHome.metaTitle',
        type: 'page',
        aliases: 'SystemSearch.aliases.dashboardPage',
        breadcrumbs: [
            {
                label: 'Pages.DashboardHome.metaTitle',
            },
        ],
        route: PostAuthRoutes.Dashboard.Dashboard,
        permissions: [PermissionName.DashboardReporting],
    },
};

export default setup;
